/**
 * Turning what an operator types into money, and back.
 *
 * The conversion works on the **digit string**, never on a float: `1.005 * 100`
 * is `100.49999999999999`, and a price that is one halala out is a price the
 * server will not charge (ADR-004). The import pipeline learned this the hard
 * way; the product form inherits the lesson rather than repeating it.
 *
 * Kept in `lib` rather than beside the form so it can be tested without
 * dragging in React, the stores, and a browser global.
 */

/** Riyals as typed → halalas. `null` when the input is not a usable amount. */
export function riyalsToHalalas(input: string): number | null {
  // Thousands separators are what a paste from a spreadsheet brings with it.
  const trimmed = input.trim().replace(/,/g, '');
  if (!/^\d*\.?\d*$/.test(trimmed) || trimmed === '' || trimmed === '.') return null;

  const [whole = '0', fraction = ''] = trimmed.split('.');
  // Pad or truncate to exactly two places on the string. Truncating rather than
  // rounding matters: rounding up would charge more than the operator typed.
  const halalas = `${whole}${fraction.padEnd(2, '0').slice(0, 2)}`;

  const value = Number(halalas);
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

export function halalasToRiyals(halalas: number): string {
  return (halalas / 100).toFixed(2);
}

/** `color=brown; size=M` → `{ color: 'brown', size: 'M' }`. */
export function parseOptions(input: string): Record<string, string> {
  const options: Record<string, string> = {};

  for (const pair of input.split(/[;,\n]/)) {
    const [name, ...rest] = pair.split('=');
    // Re-joining keeps a value that itself contains "=" intact.
    const value = rest.join('=').trim();
    if (name?.trim() && value) options[name.trim()] = value;
  }

  return options;
}

export function formatOptions(options: Record<string, string>): string {
  return Object.entries(options)
    .map(([name, value]) => `${name}=${value}`)
    .join('; ');
}

/**
 * A measure as typed — "25", "0.5", "2.25" — to thousandths of the unit (R2).
 *
 * Converted on the digit string, like a price, so "0.1" is exactly 100 and
 * never 100.00000000000001. Up to three decimals; anything else is null.
 */
export function unitsToThousandths(input: string): number | null {
  const trimmed = input.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,3})?$/.test(trimmed)) return null;
  const [whole, fraction = ''] = trimmed.split('.');
  const value = Number(whole) * 1000 + Number(fraction.padEnd(3, '0'));
  return value > 0 ? value : null;
}

/** Thousandths back to what an operator types: 25000 → "25", 500 → "0.5". */
export function thousandthsToUnits(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const whole = Math.floor(value / 1000);
  const fraction = String(value % 1000).padStart(3, '0').replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : String(whole);
}

/**
 * A percentage typed by staff — "10", "12.5", "12,5" — as basis points (1250),
 * without a float: a trade list's percentage prices real money (ADR-004, ADR-045).
 * Null for anything that is not 0 to 99.99.
 */
export function percentToBps(input: string): number | null {
  const match = /^\s*(\d{1,2})(?:[.,](\d{1,2}))?\s*$/.exec(latinDigits(input));
  if (!match) return null;
  return Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
}

/** Basis points back to the shortest percentage that says them: 1250 → "12.5". */
export function bpsToPercent(bps: number): string {
  const whole = Math.floor(bps / 100);
  const fraction = bps % 100;
  if (fraction === 0) return String(whole);
  return `${whole}.${String(fraction).padStart(2, '0').replace(/0$/, '')}`;
}

/** Arabic-Indic and Persian digits, and the Arabic decimal separator, as typed on an Arabic keyboard. */
function latinDigits(input: string): string {
  return input
    .replace(/[\u0660-\u0669]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(/[\u06f0-\u06f9]/g, (digit) => String(digit.charCodeAt(0) - 0x06f0))
    .replace(/\u066b/g, '.');
}
